"use client"

import type React from "react"

import { useState } from "react"
import { MapPin, Phone, Mail, Send, CheckCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

export default function ContactPage() {
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [formStartedAt] = useState(() => Date.now())
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
    referral: "search",
    company: "",
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    try {
      setIsSubmitting(true)
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          startedAt: formStartedAt,
          source: "contact",
          submittedAt: new Date().toISOString(),
        }),
      })

      if (!response.ok) {
        throw new Error("Submission failed")
      }

      setFormSubmitted(true)
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        subject: "",
        message: "",
        referral: "search",
        company: "",
      })
    } catch (error) {
      setSubmitError("Something went wrong. Please try again in a moment.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className="min-h-screen py-12">
      <div className="container px-4 sm:px-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Have questions, feedback, or want to collaborate? We'd love to hear from you. Fill out the form below or
            reach out directly through our contact information.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-card rounded-xl shadow-md p-8 border">
            {formSubmitted ? (
              <div className="text-center py-12">
                <div className="flex justify-center mb-4">
                  <CheckCircle className="h-16 w-16 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
                <p className="text-muted-foreground mb-6">
                  Your message has been sent successfully. We'll get back to you as soon as possible.
                </p>
                <Button onClick={() => setFormSubmitted(false)}>Send Another Message</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h2 className="text-2xl font-bold mb-6">Send Us a Message</h2>

                {submitError ? (
                  <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                    {submitError}
                  </div>
                ) : null}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={(event) => setFormData((prev) => ({ ...prev, firstName: event.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={(event) => setFormData((prev) => ({ ...prev, lastName: event.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john.doe@example.com"
                    value={formData.email}
                    onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2 mb-4">
                  <Label htmlFor="subject">Subject</Label>
                  <Select
                    value={formData.subject}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, subject: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a subject" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Inquiry</SelectItem>
                      <SelectItem value="feedback">Feedback</SelectItem>
                      <SelectItem value="collaboration">Collaboration Proposal</SelectItem>
                      <SelectItem value="support">Support</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 mb-6">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    placeholder="Your message here..."
                    rows={5}
                    value={formData.message}
                    onChange={(event) => setFormData((prev) => ({ ...prev, message: event.target.value }))}
                    required
                  />
                </div>

                <div className="sr-only" aria-hidden="true">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.company}
                    onChange={(event) => setFormData((prev) => ({ ...prev, company: event.target.value }))}
                  />
                </div>

                <div className="space-y-2 mb-6">
                  <Label>How did you hear about us?</Label>
                  <RadioGroup
                    value={formData.referral}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, referral: value }))}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="search" id="search" />
                      <Label htmlFor="search">Search Engine</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="social" id="social" />
                      <Label htmlFor="social">Social Media</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="friend" id="friend" />
                      <Label htmlFor="friend">Friend Recommendation</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="other" id="other" />
                      <Label htmlFor="other">Other</Label>
                    </div>
                  </RadioGroup>
                </div>

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  <Send className="mr-2 h-4 w-4" /> {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            )}
          </div>

          {/* Contact Information */}
          <div>
            <div className="bg-accent rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <MapPin className="h-6 w-6 text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="font-bold mb-1">Our Office</h3>
                    <p className="text-muted-foreground">
                      Natouris,
                      <br />
                      Kandy,
                      <br />
                      Sri Lanka
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Phone className="h-6 w-6 text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="font-bold mb-1">Phone</h3>
                    {/* <p className="text-muted-foreground">+1 (555) 123-4567</p> */}
                    <p className="text-muted-foreground text-sm">Monday-Friday, 9AM-5PM EST</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Mail className="h-6 w-6 text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="font-bold mb-1">Email</h3>
                    <p className="text-muted-foreground">info@natouris.com</p>
                    <p className="text-muted-foreground">support@natouris.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="bg-card rounded-xl shadow-md p-8 border">
              <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-bold mb-2">How can I collaborate with Natouris?</h3>
                  <p className="text-muted-foreground">
                    We're always open to collaborations with brands, destinations, and fellow content creators. Please
                    use our contact form and select "Collaboration Proposal" as the subject.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold mb-2">Do you offer personalized travel planning services?</h3>
                  <p className="text-muted-foreground">
                    Yes, our team offers custom itinerary planning for select destinations. Contact us with your
                    requirements for more information and pricing.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold mb-2">How can I submit a guest post?</h3>
                  <p className="text-muted-foreground">
                    We welcome high-quality guest contributions from experienced travelers. Please review our submission
                    guidelines on the About page before contacting us.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold mb-2">I found an error in an article. How can I report it?</h3>
                  <p className="text-muted-foreground">
                    We appreciate your help in maintaining accurate information. Please contact us with the article URL
                    and details about the error.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map */}
        {/* <div className="mt-12 bg-white rounded-xl shadow-md p-4">
          <div className="relative h-[400px] rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center">
            <p className="text-gray-500">Interactive map would be displayed here</p>
            // In a real application, you would integrate a map library like Mapbox or Google Maps here
          </div>
        </div> */}
      </div>
    </main>
  )
}
